import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cityshor_cart") || "[]"); }
    catch { return []; }
  });
  const validatedRef = useRef(false);

  // Persist the cart WITHOUT large inline images: product photos are multi-MB
  // base64 strings and blow past the ~5 MB localStorage quota, which threw
  // inside this effect and broke Add to Cart / Buy Now.
  useEffect(() => {
    try {
      const slim = items.map((i) => ((i.image_base64 || "").length > 150000 ? { ...i, image_base64: "" } : i));
      localStorage.setItem("cityshor_cart", JSON.stringify(slim));
    } catch (e) {
      try { localStorage.setItem("cityshor_cart", JSON.stringify(items.map((i) => ({ ...i, image_base64: "" })))); } catch { /* storage unavailable */ }
    }
  }, [items]);

  // On first mount and whenever the tab regains focus, prune expired / removed products.
  useEffect(() => {
    const prune = async () => {
      const current = JSON.parse(localStorage.getItem("cityshor_cart") || "[]");
      if (!current.length) return;
      try {
        const { data } = await axios.post(`${API}/cart/validate`, {
          product_ids: current.map((c) => c.product_id),
        });
        const valid = new Set(data.valid_ids || []);
        if ((data.removed || []).length > 0) {
          setItems((prev) => prev.filter((p) => valid.has(p.product_id)));
          const names = (data.removed || []).map((r) => r.name).filter(Boolean).slice(0, 3);
          const label = names.length ? names.join(", ") : "some items";
          toast.info(`${label} left the bazaar and were removed from your cart.`);
        }
      } catch {
        // network failure — do nothing, keep cart as-is
      }
    };
    if (!validatedRef.current) {
      validatedRef.current = true;
      prune();
    }
    const onFocus = () => prune();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const addItem = (product, qty = 1, size = "") => {
    if (product.sold_out || Number(product.stock ?? 1) <= 0) return;
    setItems((prev) => {
      const key = (p) => p.product_id === product.id && (p.size || "") === (size || "");
      const existing = prev.find(key);
      if (existing) return prev.map((p) => key(p) ? { ...p, quantity: p.quantity + qty } : p);
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image_base64: product.image_base64,
        size: size || "",
        quantity: qty,
      }];
    });
  };

  const updateQty = (product_id, quantity, size = "") => {
    if (quantity <= 0) return removeItem(product_id, size);
    setItems((prev) => prev.map((p) =>
      (p.product_id === product_id && (p.size || "") === (size || ""))
        ? { ...p, quantity }
        : p
    ));
  };

  const removeItem = (product_id, size = "") => setItems((prev) => prev.filter((p) =>
    !(p.product_id === product_id && (p.size || "") === (size || ""))
  ));
  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
